import { useState } from "react";
import { useUploader } from "../../../../contexts/uploaderContext/uploaderContext.js";
import { useToasts } from "react-toast-notifications";
import { useContent } from "contexts/contentContext/contentContext.js";
import handleAddFileUploader from "./handleAddFileUploader.js";
import { fromEvent } from "file-selector";
import useWaitForFilesModal from "./useWaitForFilesModal.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import SimpleContent from "components/Structure/ModalManager/SimpleContent.js";

export const useDragUploader = (props) => {
  const { uploaderAddFile, getUploading, setTooManyVideos } = useUploader();
  const { addToast } = useToasts();
  const { getTextByKey } = useContent();
  const { setModal } = useModal();
  const { onStart, onEnd } = useWaitForFilesModal();

  const [dragged, setDragged] = useState(false);

  let draggedThrottle = false;
  // throttle function to prevent multiple calls to setDragged or not
  const setDraggedThrottle = (value) => {
    draggedThrottle = false;
    setTimeout(() => {
      if (!draggedThrottle) {
        setDragged(value);
        draggedThrottle = true;
      }
    }, 300);
  };

  // dragging event
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (getUploading()) return;
    if (props?.additionalDragFunc) props.additionalDragFunc(e);
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragged(true);
      draggedThrottle = true;
    } else if (e.type === "dragleave") {
      setDraggedThrottle(false);
    }
  };

  // promise function to get files from dataTransferItems(folder drag and drop)
  const getFilesDataTransferItems = (dataTransferItems) => {
    const traverseFileTreePromise = (item, path = "", folder) => {
      return new Promise((resolve) => {
        if (item.isFile) {
          item.file((file) => {
            const pathString = path + file.name + "/";
            file.filepath = pathString; //save full path
            file.isFiles = item.isFile;
            folder.push(file);
            resolve(file);
          });
        } else if (item.isDirectory) {
          const dirReader = item.createReader();
          dirReader.readEntries((entries) => {
            const entriesPromises = [];
            // const subfolder = [];
            // folder.push({ name: item.name, subfolder: subfolder });
            entries.forEach((entry) => {
              const pathString = path + item.name + "/";
              entriesPromises.push(
                traverseFileTreePromise(entry, pathString, folder)
              );
            });
            resolve(Promise.all(entriesPromises));
          });
        }
      });
    };

    const files = [];
    return new Promise((resolve, reject) => {
      const entriesPromises = [];

      for (let it of dataTransferItems)
        entriesPromises.push(
          traverseFileTreePromise(it.webkitGetAsEntry(), "", files)
        );

      Promise.all(entriesPromises).then((entries) => {
        resolve(files);
      });
    });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (getUploading()) return;
    await onStart();
    setDragged(false);
    if (props?.additionalDropFunc) props.additionalDropFunc(e);
    try {
      const filesArray = await fromEvent(e);
      const tooManyVideos =
        filesArray.filter((file) => file.type.includes("video")).length > 50;
      if (tooManyVideos) {
        setTooManyVideos(true);
      }
      filesArray?.forEach(async (file) => {
        await handleAddFileUploader(
          file,
          uploaderAddFile,
          addToast,
          getTextByKey,
          file.filepath || file.path,
          tooManyVideos
        );
      });
      onEnd();
    } catch (error) {
      console.log(error);
      setModal({
        modal: SimpleContent,
        props: {
          title: getTextByKey("drag-folder-issue-title"),
          message: getTextByKey("drag-folder-issue-message"),
        },
      });
      onEnd();
    }
  };

  return { handleDrop, handleDrag, dragged };
};
